import { DateTime, Info } from 'luxon';
import cc from 'classcat';
import { DisabledDaysWithMessage } from '../../../commonTypes/acs';
import { HolidayDto } from '../../../commonTypes/organization.types';
import { isHoliday } from '../../../util/date/dateHelper';
import { useTranslation } from '../../../hooks/useTranslation';
import { LeaveRequestDto, LeaveDurationTypeEnum } from '../../../commonTypes/leaveRequest.types';

export type CalendarModalAlignment = 'left' | 'right';

interface ICalendar {
    displayDate: DateTime;
    startDate?: DateTime | null;
    endDate?: DateTime | null;
    singleDate?: DateTime;
    onClick: (date: DateTime) => void;
    changeMonth: (date: DateTime, nextBackAction?: string) => void;
    enablePastDays?: boolean;
    activeArrows?: {
        active?: boolean;
        firstCalendarLeftButton?: boolean;
        firstCalendarRightButton?: boolean;
        secondCalendarLeftButton?: boolean;
        secondCalendarRightButton?: boolean;
    };
    disabledDays?: DisabledDaysWithMessage[];
    activeModal?: boolean;
    modalAlignment?: CalendarModalAlignment;
    disabledWeekends?: boolean;
    disableAfterEndDate?: boolean;
    holidays?: HolidayDto[];
    leaveRequests?: LeaveRequestDto[];
    isMedicalLeave?: boolean;
    isEndMedicalLeave?: boolean;
    fixedStartDate?: string;
}

interface IallDates {
    message: string;
    luxonDate: DateTime;
}

export const Calendar = ({
    displayDate,
    startDate,
    endDate,
    onClick,
    changeMonth,
    singleDate,
    activeArrows,
    disabledDays,
    enablePastDays = false,
    activeModal = false,
    modalAlignment = 'right',
    disabledWeekends = true,
    disableAfterEndDate = false,
    holidays,
    leaveRequests,
    isMedicalLeave = false,
    isEndMedicalLeave = false,
    fixedStartDate,
}: ICalendar) => {
    const { t } = useTranslation('leaveRequest');

    const isDateWithinLeaveRequest = (date: DateTime) => {
        if (!leaveRequests || leaveRequests.length === 0) {
            return false;
        }

        const fullDayLeaveRequests = leaveRequests.filter(lr => lr.leaveDurationType === LeaveDurationTypeEnum.FULL_DAY);
        if (fullDayLeaveRequests.length === 0) {
            return false;
        }
        const normalizedDay = date.startOf('day');
        return fullDayLeaveRequests.some(lr => {
            try {
                const from = DateTime.fromISO(lr.fromDate).startOf('day');
                const to = DateTime.fromISO(lr.toDate).endOf('day');
                return normalizedDay >= from && normalizedDay <= to;
            } catch (e) {
                return false;
            }
        });
    };

    const renderEmptyDays = () => {
        const numberOfEmptyFields = DateTime.local(displayDate.year, displayDate.month, 1).weekday - 1;

        return [...Array(numberOfEmptyFields)].map((el, i) => (
            <span key={i} className="date-range-picker__day">
                {el}
            </span>
        ));
    };

    const renderDays = () => {
        let allDates: IallDates[] = [];
        let monthDate = DateTime.local(displayDate.year, displayDate.month, 1);

        while (monthDate.month === displayDate.month) {
            let datesTemaplte: IallDates = {
                message: '',
                luxonDate: monthDate,
            };
            allDates.push(datesTemaplte);
            monthDate = monthDate.plus({ days: 1 });
        }

        disabledDays?.forEach((item: DisabledDaysWithMessage) => {
            item.dates.forEach((day: string) => {
                const disabledDay = DateTime.fromISO(day);
                let indexOfDisabledDay = disabledDay.day - 1;

                if (indexOfDisabledDay >= 0 && indexOfDisabledDay < allDates.length) {
                    if (allDates[indexOfDisabledDay]?.luxonDate.month === disabledDay.month && disabledDay > DateTime.now()) {
                        allDates[indexOfDisabledDay].message = item.message;
                    }
                    if (DateTime.now().hasSame(disabledDay, 'day') && item.message === t('youHaveLeaveRequest')) {
                        allDates[indexOfDisabledDay].message = item.message;
                    }
                }
            });
        });

        return allDates.map((date: IallDates, i: number) => {
            const isBeforeFixedStart = isEndMedicalLeave && fixedStartDate && date.luxonDate < DateTime.fromISO(fixedStartDate).startOf('day');

            const isFixedStartDate =
                isEndMedicalLeave && fixedStartDate && date.luxonDate.hasSame(DateTime.fromISO(fixedStartDate).startOf('day'), 'day');

            return (
                <button
                    key={i}
                    className={cc([
                        'date-range-picker__day',
                        {
                            'date-range-picker__day--selected-start': startDate && date.luxonDate.hasSame(startDate, 'day'),
                        },
                        {
                            'date-range-picker__timelog-day': date.message,
                        },
                        {
                            'date-range-picker__weekdays': date.luxonDate.weekday === 6 || date.luxonDate.weekday === 7,
                        },
                        {
                            'date-range-picker__day--selected-end': endDate && date.luxonDate.hasSame(endDate, 'day'),
                        },
                        {
                            monday: endDate && date.luxonDate.hasSame(endDate, 'day') && date.luxonDate.weekday === 1,
                        },
                        {
                            'date-range-picker__day--last-day-in-month':
                                (startDate &&
                                    date.luxonDate > startDate &&
                                    endDate &&
                                    date.luxonDate < endDate.startOf('day') &&
                                    date.luxonDate.day === 31) ||
                                date.luxonDate.day === 30,
                        },
                        {
                            'date-range-picker__day--first-day-in-month':
                                startDate &&
                                date.luxonDate > startDate &&
                                endDate &&
                                date.luxonDate < endDate.startOf('day') &&
                                date.luxonDate.day === 1,
                        },
                        {
                            'date-range-picker__day--between':
                                startDate && date.luxonDate > startDate && endDate && date.luxonDate < endDate.startOf('day'),
                        },
                        {
                            'date-range-picker__day--monday':
                                startDate &&
                                date.luxonDate > startDate &&
                                endDate &&
                                date.luxonDate < endDate.startOf('day') &&
                                date.luxonDate.weekday === 1,
                        },
                        {
                            'date-range-picker__day--sunday':
                                startDate &&
                                date.luxonDate > startDate &&
                                endDate &&
                                date.luxonDate < endDate.startOf('day') &&
                                date.luxonDate.weekday === 7,
                        },
                        { 'date-range-picker__day--today': DateTime.now().startOf('day').equals(date.luxonDate) },
                        {
                            'date-range-picker__day--today--ongoing':
                                DateTime.now().startOf('day').equals(date.luxonDate) &&
                                startDate &&
                                endDate &&
                                startDate.startOf('day') <= DateTime.now().startOf('day') &&
                                DateTime.now().startOf('day') <= endDate.startOf('day'),
                        },
                        {
                            'date-range-picker__day--disable':
                                (!isMedicalLeave && !enablePastDays && date.luxonDate < DateTime.now().startOf('day')) ||
                                (isEndMedicalLeave && isBeforeFixedStart),
                        },
                        { 'date-range-picker__day--disable': disableAfterEndDate && endDate && date.luxonDate > endDate.startOf('day') },
                        { 'date-range-picker__day--single': endDate === null || startDate?.toMillis() === endDate?.toMillis() },
                        { 'date-range-picker__day--single-date-selected': singleDate && date.luxonDate.hasSame(singleDate, 'day') },
                        {
                            'date-range-picker__day--single-date-selected--weekend':
                                singleDate &&
                                date.luxonDate.hasSame(singleDate, 'day') &&
                                !disabledWeekends &&
                                (date.luxonDate.weekday === 6 || date.luxonDate.weekday === 7),
                        },
                        { 'date-range-picker__day--holiday': isHoliday(date.luxonDate, holidays) },
                        { 'date-range-picker__day--day-off': isDateWithinLeaveRequest(date.luxonDate) },
                        {
                            'date-range-picker__day--fixed-start': isFixedStartDate,
                        },
                    ])}
                    type="button"
                    onClick={() => {
                        if (isEndMedicalLeave && isBeforeFixedStart) {
                            return false;
                        }

                        const isDayOff =
                            date.message ||
                            date.luxonDate.weekday === 6 ||
                            date.luxonDate.weekday === 7 ||
                            isHoliday(date.luxonDate, holidays) ||
                            isDateWithinLeaveRequest(date.luxonDate);
                        if (isDayOff) {
                            return disabledWeekends ? false : onClick(date.luxonDate);
                        } else {
                            return onClick(date.luxonDate);
                        }
                    }}
                    disabled={
                        (isEndMedicalLeave && isBeforeFixedStart) ||
                        (isEndMedicalLeave && isFixedStartDate) ||
                        (!isMedicalLeave && !enablePastDays && date.luxonDate < DateTime.now().startOf('day')) ||
                        (disableAfterEndDate && endDate && date.luxonDate > endDate.startOf('day')) ||
                        (disabledWeekends && (date.luxonDate.weekday === 6 || date.luxonDate.weekday === 7)) ||
                        isHoliday(date.luxonDate, holidays) ||
                        isDateWithinLeaveRequest(date.luxonDate) ||
                        !!date.message
                    }
                >
                    {date.message && (
                        <>
                            <span
                                className={cc([
                                    'timelog-day-tooltip',
                                    {
                                        'timelog-day-tooltip--selected':
                                            startDate &&
                                            date.luxonDate > startDate &&
                                            endDate &&
                                            date.luxonDate < endDate.startOf('day') &&
                                            date.message === t('youHaveLeaveRequest'),
                                    },
                                ])}
                            ></span>
                            <span className="tooltip">{date.message}</span>
                        </>
                    )}
                    {singleDate && date.luxonDate.hasSame(singleDate, 'day') && <span className="selected-start"></span>}
                    {startDate && date.luxonDate.hasSame(startDate, 'day') && <span className="selected-start"></span>}
                    {startDate && date.luxonDate > startDate && endDate && date.luxonDate < endDate.startOf('day') && (
                        <span className="selected-between"></span>
                    )}
                    {endDate && date.luxonDate.hasSame(endDate, 'day') && <span className="selected-end"></span>}

                    {isFixedStartDate && <span className="date-range-picker__fixed-start-indicator" title="Fixed start date"></span>}

                    <span className="day-date">{date.luxonDate.day}</span>
                </button>
            );
        });
    };

    return (
        <div
            className={cc([
                'date-range-picker__calendar date-range-picker__calendar-view',
                { 'date-range-picker__calendar-view--active-modal': activeModal },
                `date-range-picker__calendar-view--active-modal--align-${modalAlignment}`,
                { 'date-range-picker__calendar-view--end-medical-leave': isEndMedicalLeave },
            ])}
        >
            {activeArrows?.active ? (
                <div className="date-range-picker__month">
                    {activeArrows.firstCalendarLeftButton && (
                        <button type="button" onClick={() => changeMonth(displayDate, 'left')}>
                            <img src="/img/common/arrow-left.svg" alt="Arrow" />
                        </button>
                    )}
                    <p className="date-range-picker__month-name">
                        {displayDate.toFormat('MMMM')} {displayDate.toFormat('yyyy')}
                    </p>
                    {activeArrows.secondCalendarRightButton && (
                        <button type="button" onClick={() => changeMonth(displayDate, 'right')}>
                            <img src="/img/common/arrow-right.svg" alt="Arrow" />
                        </button>
                    )}
                </div>
            ) : (
                <div className="date-range-picker__month">
                    <button type="button" onClick={() => changeMonth(displayDate.minus({ months: 1 }))}>
                        <img src="/img/common/arrow-left.svg" alt="Arrow" />
                    </button>
                    <p className="date-range-picker__month-name">
                        {displayDate.toFormat('MMMM')} {displayDate.toFormat('yyyy')}
                    </p>
                    <button type="button" onClick={() => changeMonth(displayDate.plus({ months: 1 }))}>
                        <img src="/img/common/arrow-right.svg" alt="Arrow" />
                    </button>
                </div>
            )}

            <div className="date-range-picker__days">
                {Info.weekdays('narrow').map((weekDay, i) => (
                    <span key={i} className="date-range-picker__day">
                        {weekDay}
                    </span>
                ))}
            </div>
            <div className="date-range-picker__days">
                {renderEmptyDays()}
                {renderDays()}
            </div>
        </div>
    );
};
